// Hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import type { HashtagType } from '@/utils/hooks/swr/useHashtags';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';

import type { DashboardType } from '@/types/dashboardType';
import type { SocialProfileType } from '@/types/socialProfileType';

// Lib
import { REPORT_TYPES } from '@/lib/reports/reportFormTypesConfig';

// Types
import type { SocialProfilesAndGroupsCondensedListProps } from '@/components/composite/SocialProfiles/SocialProfilesAndGroupsCondensedList';
// Components
import CompetitorsItem from '@/components/pages/reports/components/Items/Types/CompetitorsItem';
import DashboardItem from '@/components/pages/reports/components/Items/Types/DashboardItem';
import HashtagsItem from '@/components/pages/reports/components/Items/Types/HashtagsItem';
import SocialProfileItem from '@/components/pages/reports/components/Items/Types/SocialProfileItem';

type ReportContentPreviewAccountValueType = {
    type: typeof REPORT_TYPES.ACCOUNT;
    social_profiles?: SocialProfileType[];
};

type ReportContentPreviewDashboardValueType = {
    type: typeof REPORT_TYPES.DASHBOARD;
    dashboards?: DashboardType[];
};

type ReportContentPreviewHashtagValueType = {
    type: typeof REPORT_TYPES.HASHTAG;
    hashtags?: HashtagType[];
};

type ReportContentPreviewCompetitorValueType = {
    type: typeof REPORT_TYPES.COMPETITOR;
    social_profiles?: SocialProfileType[];
};

type ReportContentPreviewProps = {
    value:
        | ReportContentPreviewAccountValueType
        | ReportContentPreviewDashboardValueType
        | ReportContentPreviewHashtagValueType
        | ReportContentPreviewCompetitorValueType;
    size: SocialProfilesAndGroupsCondensedListProps['size'];
};

const ReportContentPreview = ({ value, size = 'sm' }: ReportContentPreviewProps) => {
    const { socialProfiles } = useSocialProfiles({
        revalidateIfStale: false
    });
    const { spGroups } = useSPGroups({
        revalidateIfStale: false
    });

    const dashboards = 'dashboards' in value ? value.dashboards : null;

    const dashboardSpId = dashboards?.length ? dashboards[0]?.data?.social_profiles : null;

    const SpForDashboard = dashboardSpId
        ? dashboardSpId?.flatMap((sp) => socialProfiles?.filter((item) => item?.id === sp))
        : null;

    const dashboardSpGroupsId = dashboards?.length ? dashboards[0]?.data?.groups : null;

    const groupsForDashboard = dashboardSpGroupsId
        ? dashboardSpGroupsId?.flatMap((group) => spGroups?.filter((item) => item?.id === group))
        : null;

    switch (value.type) {
        case REPORT_TYPES.ACCOUNT:
            return <SocialProfileItem item={value?.social_profiles?.[0]} size={size} />;
        case REPORT_TYPES.COMPETITOR:
            return <CompetitorsItem item={value?.social_profiles?.[0]} size={size} />;
        case REPORT_TYPES.DASHBOARD:
            return (
                <DashboardItem
                    socialProfiles={SpForDashboard}
                    spGroups={groupsForDashboard}
                    item={value?.dashboards?.[0]}
                    size={size}
                />
            );
        case REPORT_TYPES.HASHTAG:
            return <HashtagsItem item={value?.hashtags?.[0]} />;
    }
};

export default ReportContentPreview;
